/**
 * The breadcrumb, assembled from whatever the router matched.
 *
 * Each route declares its own crumb in a `handle`, so the trail is a property
 * of the route tree rather than something every screen has to remember to
 * render. A route with no crumb simply contributes nothing.
 *
 * @module components/shell/RouteBreadcrumb
 */

import React from "react";

import { useMatches } from "react-router";

import { Breadcrumb, type Crumb } from "@/components/ui";
import { useOptionalWorkspaceContext } from "@/router/workspace-context";

/** One step a route contributes: its own label, or an ancestor it sits under. */
export interface RouteCrumb {
  label: string;
  /** Where the ancestor links to. The route's own crumb omits it. */
  to?: string;
  /** The label stands in for a name still loading. */
  pending?: boolean;
}

/**
 * What a crumb function may look things up in.
 *
 * A route knows its params, not what they name. The programme title lives in
 * the workspace the shell already loaded, so the crumb asks for it rather than
 * fetching anything of its own; before that load answers it has no title, and
 * the route falls back to its static label.
 */
export interface CrumbContext {
  programmeTitle: (programmeId: string | undefined) => string | undefined;
  /** For the one address that is two different consoles. */
  isPlatformAdmin: boolean;
}

/** The function form of a crumb, named so a builder can return exactly it. */
export type CrumbFn = (
  params: Record<string, string | undefined>,
  context: CrumbContext,
) => string | RouteCrumb[] | undefined;

/** What a route may put in its `handle` to appear in the trail. */
export interface RouteCrumbHandle {
  /**
   * A fixed label, one derived from the match's own params, or a trail.
   *
   * The trail form is for a route that is a sibling of its parent in the tree
   * rather than a child: `/programmes/:id` is declared beside `/programmes`,
   * not inside it, so the router never matches the list and the crumb has to
   * name it.
   */
  crumb?: string | RouteCrumb[] | CrumbFn;
}

function crumbsFor(
  handle: unknown,
  params: Record<string, string | undefined>,
  context: CrumbContext,
): RouteCrumb[] {
  const candidate = (handle as RouteCrumbHandle | undefined)?.crumb;
  const resolved = typeof candidate === "function" ? candidate(params, context) : candidate;
  if (!resolved) {
    return [];
  }
  return typeof resolved === "string" ? [{ label: resolved }] : resolved;
}

/**
 * The trail for a route under a programme, which every one of them shares.
 *
 * `/programmes/:programmeId` is declared beside `/programmes` rather than
 * inside it, so the list is never matched and has to be named here.
 *
 * @param tail - What sits under the programme, e.g. "Module". Omit for the
 *   programme itself.
 * @returns A crumb function for the route's `handle`.
 */
export function programmeCrumb(tail?: string): CrumbFn {
  return (params, { programmeTitle }) => [
    { label: "Programmes", to: "/programmes" },
    {
      label: programmeTitle(params.programmeId) ?? "Programme",
      pending: programmeTitle(params.programmeId) === undefined,
      // The overview's first section, not the bare path: the programme is a
      // layout with children, so its own address only redirects. Named whether
      // or not anything sits under it, because a section below can make the
      // programme a link without the crumb knowing.
      to: `/programmes/${params.programmeId ?? ""}/overview/identity`,
    },
    ...(tail
      ? [
          {
            label: tail,
            // A change case is itself a layout with children, so its crumb sits
            // mid-trail and needs an address of its own, the same shape as the
            // programme's. A tail that is the page you are on has its link
            // stripped.
            ...(params.changeCaseId
              ? {
                  to: `/programmes/${params.programmeId ?? ""}/changes/${params.changeCaseId}/overview/identity`,
                }
              : {}),
          },
        ]
      : []),
  ];
}

/**
 * The trail the router matched, outermost first.
 *
 * The breadcrumb renders it and the document title names the page from it, so
 * both read one resolution of the route handles rather than two.
 *
 * @returns Every crumb the matched routes declare, each with the address it
 *   links to where it has one.
 */
export function useRouteCrumbs(): RouteCrumb[] {
  const matches = useMatches();
  const workspace = useOptionalWorkspaceContext();
  const context: CrumbContext = {
    programmeTitle: (programmeId) =>
      workspace?.platformProgrammes.find((programme) => programme.id === programmeId)?.title,
    isPlatformAdmin: workspace?.profile.role === "platform-admin",
  };
  return matches.flatMap((match) =>
    crumbsFor(match.handle, match.params as Record<string, string | undefined>, context).map(
      // A crumb naming its own address keeps it. Otherwise the route's own
      // crumb, which is the last one it declares, takes the matched path, and
      // an ancestor with nothing to point at links nowhere.
      (crumb, index, all) => ({
        label: crumb.label,
        to: crumb.to ?? (index === all.length - 1 ? match.pathname : undefined),
        ...(crumb.pending ? { pending: true } : {}),
      }),
    ),
  );
}

/**
 * @example
 * ```tsx
 * { path: "portfolio", handle: { crumb: "Portfolio" }, Component: PortfolioRoute }
 * ```
 */
export function RouteBreadcrumb(): React.ReactElement | null {
  const crumbs = useRouteCrumbs();

  if (crumbs.length === 0) {
    return null;
  }
  // The last crumb is the page itself, which is not a link to where you
  // already are. It is not always the last match: an index route usually
  // contributes no crumb of its own.
  const items: Crumb[] = crumbs.map((crumb, index) =>
    index === crumbs.length - 1 ? { label: crumb.label } : crumb,
  );
  return <Breadcrumb items={items} data-testid="breadcrumb" />;
}
